import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';

import { TranslateService } from '@ngx-translate/core';

import { SeoImageUrlPipe } from '../cloudinary-url/seo-image-url.pipe';
import { Seo } from '../data/models/components/seo';

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private titleService: Title,
    private translateService: TranslateService,
    private meta: Meta,
    private seoImageUrlPipe: SeoImageUrlPipe,
    private http: HttpClient
  ) {
    this.http.get('manifest.webmanifest').subscribe((manifest: any) => {
      this.setIcons(manifest.icons);
      this.enableApplePwaSupport(manifest);
    });
  }

  set(seo: Seo, category: string): void {
    const company = this.translateService.instant('company.name');
    const twitterCreator = '@PlaceMe7';
    this.setTitle(this.computeTitle(company, seo?.title));
    this.setDescription(seo?.description || '');
    this.setImage(seo, category);
    this.setURL(this.getCanonicalURL());
    this.setSiteName(company);
    this.setAuthor(company);
    this.setType('website');
    this.setTwitterCreator(twitterCreator);
  }

  private computeTitle(prefix: string, title: string): string {
    const separator = ' | ';
    const titleItems = [prefix];
    if (title) {
      titleItems.push(title);
    }
    return titleItems.join(separator);
  }

  private enableApplePwaSupport(manifest: any): void {
    this.meta.updateTag({ name: 'theme-color', content: manifest.theme_color });
    this.meta.updateTag({ name: 'apple-mobile-web-app-capable', content: 'yes' });
    this.meta.updateTag({ name: 'apple-mobile-web-app-title', content: manifest.name });
    this.meta.updateTag({ name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' });
  }

  private getCanonicalLink(): HTMLLinkElement {
    let link: HTMLLinkElement = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    return link;
  }

  private getCanonicalURL(): string {
    let canonicalUrl = this.document.location.href;
    const hasTrailingSlash = canonicalUrl.match(/.*$\//);
    if (!hasTrailingSlash) {
      canonicalUrl = `${canonicalUrl}/`;
    }
    return canonicalUrl;
  }

  private setAuthor(author: string): void {
    this.meta.updateTag({ name: 'author', content: author });
  }

  private setCanonicalURL(canonicalUrl: string): void {
    this.getCanonicalLink().setAttribute('href', canonicalUrl);
  }

  private setDescription(description: string): void {
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ name: 'twitter:description', content: description });
  }

  private setIcons(icons: any[]): void {
    icons.forEach(icon => {
      const link: HTMLLinkElement = this.document.createElement('link');
      link.setAttribute('rel', 'apple-touch-icon');
      link.setAttribute('href', icon.src);
      link.setAttribute('sizes', icon.sizes);
      this.document.head.appendChild(link);
    });
  }

  private setImage(seo: Seo, category: string): void {
    const image = seo && this.seoImageUrlPipe.transform(seo, { text: true, format: 'rectangle', logo: true, category });
    const imageAlt = seo?.title;
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });
    this.meta.updateTag({ property: 'og:image:secure_url', content: image });
    this.meta.updateTag({ property: 'og:image:alt', content: imageAlt });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: imageAlt });
  }

  private setSiteName(siteName: string): void {
    this.meta.updateTag({ property: 'og:site_name', content: siteName });
    this.meta.updateTag({ name: 'twitter:site', content: siteName });
  }

  private setTitle(title: string): void {
    this.titleService.setTitle(title);
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ name: 'twitter:title', content: title });
  }

  private setTwitterCreator(twitterCreator: string): void {
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:creator', content: twitterCreator });
  }

  private setType(type: string): void {
    this.meta.updateTag({ property: 'og:type', content: type });
  }

  private setURL(canonicalUrl: string): void {
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'twitter:url', content: canonicalUrl });
    this.setCanonicalURL(canonicalUrl);
  }
}
